import { useMemo } from "react";
import { AdvancedMarker } from "@vis.gl/react-google-maps";
import type { Trip } from "@/api.types.v2";
import { useActiveTrips } from "@/api/trips";
import { useShortcutsStore } from "@/screens/dashboard/shortcuts.store";
import { useCarrierScope } from "@/contexts/carrier-scope-context";

interface TripRouteMarkersProps {
  zoomLevel: number;
}

interface LocationType {
  lat: number;
  lng: number;
}

interface MarkerGroup {
  location: LocationType;
  trips: Trip[];
  type: "start" | "end";
}

export function TripRouteMarkers({ zoomLevel }: TripRouteMarkersProps) {
  const { tripFilter } = useShortcutsStore();
  const { carrierIds, dispatchCenterIds } = useCarrierScope();
  const { data: tripsResponse } = useActiveTrips({
    carrierIds: carrierIds.join(","),
    dispatchCenterIds: dispatchCenterIds.join(","),
  });

  // Only show markers for trips with custom locations
  const tripsWithCustomLocations = useMemo(() => {
    const trips = tripsResponse?.data ?? [];
    return trips.filter(
      (trip) =>
        Boolean(trip.useCustomStartLocation && trip.startLocation) ||
        Boolean(trip.useCustomEndLocation && trip.endLocation)
    );
  }, [tripsResponse]);

  // Group markers by location to detect overlaps
  const markerGroups = useMemo(() => {
    const startLocationsMap = new Map<string, MarkerGroup>();
    const endLocationsMap = new Map<string, MarkerGroup>();

    tripsWithCustomLocations.forEach((trip) => {
      if (trip.useCustomStartLocation && trip.startLocation) {
        const lat = Number(trip.startLocation.lat);
        const lng = Number(trip.startLocation.lng);
        const locationKey = `${lat},${lng}`;

        if (startLocationsMap.has(locationKey)) {
          startLocationsMap.get(locationKey)?.trips.push(trip);
        } else {
          startLocationsMap.set(locationKey, {
            location: { lat, lng },
            trips: [trip],
            type: "start",
          });
        }
      }

      if (trip.useCustomEndLocation && trip.endLocation) {
        const lat = Number(trip.endLocation.lat);
        const lng = Number(trip.endLocation.lng);
        const locationKey = `${lat},${lng}`;

        if (endLocationsMap.has(locationKey)) {
          endLocationsMap.get(locationKey)?.trips.push(trip);
        } else {
          endLocationsMap.set(locationKey, {
            location: { lat, lng },
            trips: [trip],
            type: "end",
          });
        }
      }
    });

    return [...startLocationsMap.values(), ...endLocationsMap.values()];
  }, [tripsWithCustomLocations]);

  // Early return if trips are not being shown or no custom locations
  if (
    (!tripFilter.showAll && tripFilter.visibleTripIds.length === 0) ||
    tripsWithCustomLocations.length === 0
  ) {
    return null;
  }

  // Adjust marker size based on zoom level - more granular scaling
  const getMarkerSize = () => {
    if (zoomLevel < 10) return 30;
    if (zoomLevel < 12) return 36;
    if (zoomLevel < 14) return 44;
    if (zoomLevel < 16) return 52;
    return 60;
  };

  const markerSize = getMarkerSize();

  return (
    <>
      {markerGroups.map((group, index) =>
        group.trips.length === 1 ? (
          <TripRouteMarker
            key={`${group.type}-${group.trips[0].id}`}
            trip={group.trips[0]}
            markerSize={markerSize}
            location={group.location}
            type={group.type}
          />
        ) : (
          <MergedTripRouteMarker
            key={`${group.type}-group-${index}`}
            group={group}
            markerSize={markerSize}
          />
        )
      )}
    </>
  );
}

interface TripRouteMarkerProps {
  trip: Trip;
  markerSize: number;
  location: LocationType;
  type: "start" | "end";
}

function TripRouteMarker({
  trip,
  markerSize,
  location,
  type,
}: TripRouteMarkerProps) {
  // Fixed connector length
  const connectorLength = 15;

  return (
    <AdvancedMarker
      position={location}
      title={`Trip ${trip.id} ${type === "start" ? "Start" : "End"}`}
      zIndex={1000}
    >
      <div className="flex flex-col items-center">
        <div
          className="flex items-center justify-center rounded-full border-2 border-white"
          style={{
            backgroundColor: trip.color ?? "#3b82f6",
            width: markerSize,
            height: markerSize,
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        >
          <span
            className="text-white font-bold drop-shadow-md"
            style={{ fontSize: markerSize * 0.5 }}
          >
            {type === "start" ? "S" : "E"}
          </span>
        </div>
        {/* Connector line and dot */}
        <div className="flex flex-col items-center">
          <div
            className="w-0.5 bg-black"
            style={{
              height: `${connectorLength}px`,
            }}
          ></div>
        </div>
      </div>
    </AdvancedMarker>
  );
}

interface MergedTripRouteMarkerProps {
  group: MarkerGroup;
  markerSize: number;
}

function MergedTripRouteMarker({
  group,
  markerSize,
}: MergedTripRouteMarkerProps) {
  const { trips, location, type } = group;
  const connectorLength = 15;
  const totalTrips = trips.length;

  // Limit to showing max 4 colors in the merged marker
  const displayedTrips = trips.slice(0, 4);
  const hasMoreTrips = totalTrips > 4;

  // Create pie chart gradient
  const generateConicGradient = () => {
    // If only one trip, just use solid color
    if (displayedTrips.length === 1) {
      return displayedTrips[0].color ?? "#3b82f6";
    }

    // Calculate segment percentage and positions
    const segmentPercent = 100 / displayedTrips.length;

    let gradientString = "conic-gradient(";
    displayedTrips.forEach((trip, index) => {
      const color = trip.color ?? "#3b82f6";
      const startPercent = index * segmentPercent;
      const endPercent = (index + 1) * segmentPercent;

      gradientString += `${color} ${startPercent}% ${endPercent}%`;

      // Add comma if not the last segment
      if (index < displayedTrips.length - 1) {
        gradientString += ", ";
      }
    });
    gradientString += ")";

    return gradientString;
  };

  return (
    <AdvancedMarker
      position={location}
      title={`${totalTrips} ${type === "start" ? "Start" : "End"} points`}
      zIndex={1000}
    >
      <div className="flex flex-col items-center">
        <div
          className="relative rounded-full border-2 border-white overflow-hidden"
          style={{
            width: markerSize,
            height: markerSize,
            background: generateConicGradient(),
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }}
        >
          {/* Center label with lighter overlay for better visibility */}
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-black bg-opacity-15 rounded-full w-1/2 h-1/2 flex items-center justify-center">
              <span
                className="text-white font-bold drop-shadow-md"
                style={{ fontSize: markerSize * 0.4 }}
              >
                {type === "start" ? "S" : "E"}
                {hasMoreTrips && (
                  <span className="text-xs ml-1">+{totalTrips - 4}</span>
                )}
              </span>
            </div>
          </div>
        </div>

        {/* Connector line */}
        <div className="flex flex-col items-center">
          <div
            className="w-0.5 bg-black"
            style={{
              height: `${connectorLength}px`,
            }}
          ></div>
        </div>
      </div>
    </AdvancedMarker>
  );
}
